import styled from '@emotion/styled'
import { keyframes } from '@emotion/react'
import { Button as BSButton } from 'react-bootstrap'
import theme from '../../constants/theme'

export const VideoContainer = styled.div({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#000',
  zIndex: 1,
})

export const VideoOverlay = styled.div({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  background:
    'linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.1) 30%, rgba(0,0,0,0.1) 70%, rgba(0,0,0,0.6) 100%)',
  pointerEvents: 'none',
  zIndex: 2,
})

export const TopControls = styled.div({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  padding: '1.25rem',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  zIndex: 10,
})

export const ControlBubble = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  backdropFilter: 'blur(8px)',
  borderRadius: '12px',
  padding: '0.5rem 0.75rem',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
})

export const QuestionNumber = styled.span({
  color: '#fff',
  fontWeight: '600',
  fontSize: '0.875rem',
})

export const TimerBadge = styled(ControlBubble)({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  color: '#fff',
  fontWeight: '600',
  fontSize: '0.875rem',
  marginLeft: 'auto',
})

const pulse = keyframes`
  0% {
    opacity: 0.6;
    transform: translateY(-50%) scale(0.8);
  }
  100% {
    opacity: 1;
    transform: translateY(-50%) scale(1);
  }
`

export const RecordingIndicator = styled.div((props) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'white',
  fontWeight: '600',
  fontSize: '0.75rem',
  position: 'relative',
  paddingLeft: '22px',
  '&:before': {
    content: '""',
    position: 'absolute',
    left: '0',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: props.isBlinking
      ? 'rgba(239, 68, 68, 1)'
      : 'rgba(239, 68, 68, 0.8)',
    transition: 'all 0.3s ease',
    animation: props.isBlinking ? `${pulse} 0.8s infinite alternate` : 'none',
  },
}))

export const ProgressDotsContainer = styled.div({
  display: 'flex',
  gap: '4px',
})

export const ProgressDot = styled.div((props) => {
  let backgroundColor = 'rgba(255, 255, 255, 0.3)'
  if (props.active) {
    backgroundColor = props.current
      ? theme.brandColorOrange
      : 'rgba(255, 255, 255, 0.9)'
  }
  return {
    width: '4px',
    height: '4px',
    borderRadius: '2px',
    backgroundColor,
    transition: 'all 0.3s ease',
    transform: props.current ? 'scaleX(1.5)' : 'scaleX(1)',
  }
})

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const fadeOut = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-20px);
  }
`

export const QuestionCard = styled.div((props) => ({
  position: 'fixed',
  bottom: '100px',
  left: '16px',
  right: '16px',
  backgroundColor: 'rgba(26, 31, 44, 0.95)',
  backdropFilter: 'blur(12px)',
  borderRadius: '16px',
  boxShadow: '0 10px 15px -5px rgba(0, 0, 0, 0.1)',
  maxHeight: '40vh',
  overflowY: 'auto',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  zIndex: 50,
  display: 'block',
  animation: props.isAnimating
    ? `${fadeOut} 0.3s ease forwards`
    : `${fadeIn} 0.4s ease`,
}))

export const QuestionHeader = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '12px',
  padding: '1.25rem 1.25rem 0.5rem 1.25rem',
})

export const QuestionLabelContainer = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
})

export const QuestionLabel = styled.h3({
  fontSize: '0.875rem',
  fontWeight: '600',
  color: 'rgba(255, 255, 255, 0.8)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  margin: 0,
})

export const QuestionText = styled.p({
  fontSize: '1rem',
  fontWeight: '500',
  color: 'white',
  lineHeight: '1.5',
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
  maxWidth: '100%',
  padding: '0 1.25rem 1.25rem 1.25rem',
  margin: 0,
})

export const ActionButtonsContainer = styled.div({
  position: 'fixed',
  bottom: '24px',
  left: '16px',
  right: '16px',
  display: 'flex',
  justifyContent: 'center',
  zIndex: 10,
})

export const ButtonsWrapper = styled.div({
  display: 'flex',
  justifyContent: 'center',
  gap: '10px',
})

export const ModernButton = styled(BSButton)((props) => {
  const isPrimary = props.className && props.className.includes('primary')
  return {
    borderRadius: '10px',
    height: '2.75rem',
    padding: '0 1.25rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '600',
    fontSize: '0.9rem',
    transition: 'all 0.2s ease',
    backgroundColor: isPrimary
      ? theme.brandColorOrange
      : 'rgba(255, 255, 255, 0.1)',
    borderColor: 'transparent',
    color: 'white',
    '&:hover': {
      backgroundColor: isPrimary ? '#065b88' : 'rgba(255, 255, 255, 0.15)',
      transform: 'translateY(-1px)',
    },
    '&:active': {
      transform: 'translateY(1px)',
    },
    '&:disabled': {
      backgroundColor: isPrimary ? '#3991bb' : 'rgba(255, 255, 255, 0.05)',
      opacity: 0.7,
      cursor: 'not-allowed',
    },
  }
})

export const ModernIconButton = styled(ModernButton)({
  width: '2.75rem',
  padding: 0,
  borderRadius: '10px',
})

export const DestructiveButton = styled(ModernButton)({
  backgroundColor: '#ef4444',
  borderColor: 'transparent',
  '&:hover': {
    backgroundColor: '#dc2626',
    transform: 'translateY(-1px)',
  },
  '&:active': {
    backgroundColor: '#b91c1c',
    transform: 'translateY(1px)',
  },
  '&:focus, &.btn:focus, &.btn-primary:focus': {
    boxShadow: '0 0 0 0.25rem rgba(239, 68, 68, 0.4)',
    borderColor: '#b91c1c',
    outline: 'none',
  },
})

export const OutlineIconButton = styled(ModernIconButton)({
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  backdropFilter: 'blur(8px)',
  border: `1px solid ${theme.brandColorOrange}`,
  color: 'white',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    borderColor: theme.brandColorOrange,
    boxShadow: `0 3px 10px rgba(0, 0, 0, 0.4), 0 0 0 1px ${theme.brandColorOrange}80`,
    transform: 'translateY(-1px)',
  },
  '&:active': {
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    transform: 'translateY(1px)',
    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.2)',
  },
})

export const PauseButton = styled(ModernIconButton)({
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  backdropFilter: 'blur(8px)',
  border: '1px solid #FFB800',
  color: 'white',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    borderColor: '#FFC933',
    boxShadow:
      '0 3px 10px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 184, 0, 0.5)',
    transform: 'translateY(-1px)',
  },
  '&:active': {
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    transform: 'translateY(1px)',
    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.2)',
  },
  '&:disabled': {
    opacity: 0.7,
    cursor: 'not-allowed',
  },
})

export const ResumeButton = styled(ModernButton)({
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  backdropFilter: 'blur(8px)',
  border: '1px solid #10B981',
  color: 'white',
  padding: '0 1.25rem',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
  transition: 'all 0.2s ease',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    borderColor: '#34D399',
    boxShadow:
      '0 3px 10px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(16, 185, 129, 0.5)',
    transform: 'translateY(-1px)',
  },
  '&:active': {
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    transform: 'translateY(1px)',
    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.2)',
  },
})

export const PermissionOverlay = styled.div({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(0, 0, 0, 0.85)',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '2rem',
  zIndex: 100,
  backdropFilter: 'blur(6px)',
})

export const PermissionMessage = styled.p({
  color: 'white',
  fontSize: '1.125rem',
  textAlign: 'center',
  marginBottom: '2rem',
  lineHeight: 1.6,
  maxWidth: '400px',
})

export const PermissionButton = styled(ModernButton)({
  backgroundColor: theme.brandColorOrange,
  borderColor: 'transparent',
  padding: '0.75rem 1.5rem',
  fontSize: '1rem',
  fontWeight: 'bold',
  '&:hover': {
    backgroundColor: '#065b88',
    transform: 'translateY(-1px)',
  },
})
